import { Link, createFileRoute } from "@tanstack/react-router";
import { getSource, useSources } from "../../sources";
import { useActiveServer } from "../../lib/activeServer";
import { useServerActivity } from "../../lib/activityHub";
import { useNavServers } from "../../lib/navServers";
import { useServerHubs } from "../../lib/serverHubs";
import { useSourceConnection } from "../../lib/useConnection";
import { FilmstripIcon, ServerIcon } from "../../components/icons";
import { LibraryCard } from "../../components/library/library-card";
import { EmptyState, LoadingState } from "../../components/page-state";
import { ServerUnavailableState } from "../../components/server-unavailable";
import { lastSeenIfOffline } from "../../lib/servers";

export const Route = createFileRoute("/_authed/library/")({
  component: LibraryIndex,
});

/**
 * The ACTIVE server's libraries (the old rail's category list, now a page).
 * Data rides the shared ["nav","servers"] cache — pass the FULL source list
 * to useNavServers and pick the active entry, so this page, the chip and
 * the activity menu keep one cache entry warm between them.
 */
function LibraryIndex() {
  const { server, servers, isLoading: serversLoading } = useActiveServer();
  const { sources } = useSources();
  const nav = useNavServers(sources);
  const entry = nav.data?.find((s) => s.id === server?.id);

  // Card artwork rides the same hubs cache Home warms: each library's
  // recently-added hub donates its first backdrop (poster as fallback).
  // Legacy servers (no per-library hubs) just get the gradient tile.
  const source = server?.online ? (getSource(server.id) ?? null) : null;
  const { hubs } = useServerHubs(source);
  // Art URLs need the asset token — re-render when the connection state
  // moves (direct page loads race the token otherwise).
  useSourceConnection(source);
  // Live scans per library (the activity socket every owned online server
  // already holds): the tile shows the stage's ring and words while one runs.
  const activity = useServerActivity(server?.online ? server.id : null);
  const artFor = (libraryId: string): string | undefined => {
    if (!source) return undefined;
    const hub = hubs.find((h) => h.libraryId === libraryId);
    for (const item of hub?.items ?? []) {
      const art = source.imageUrl(item, "backdrop", 780);
      if (art) return art;
    }
    const first = hub?.items[0];
    return first ? source.imageUrl(first, "poster", 342) || undefined : undefined;
  };

  // Nothing claimed: the whole page is the empty state — a "Library" heading
  // over it would head nothing, so skip the shell (same bare treatment as Home).
  if (servers.length === 0 && !serversLoading) {
    return (
      <EmptyState
        icon={<ServerIcon size={28} />}
        title="No libraries yet"
        action={
          <Link to="/claim" className="rago-btn rago-btn-fit">
            Connect a server
          </Link>
        }
      >
        Connect a server to get started.
      </EmptyState>
    );
  }

  let body: React.ReactNode;
  if (servers.length === 0) {
    body = <LoadingState minH="min-h-[40vh]" />;
  } else if (server && !server.online) {
    body = (
      <ServerUnavailableState
        compact
        serverId={server.id}
        serverName={server.name}
        owned={server.owned}
        lastSeenAt={lastSeenIfOffline(server)}
      />
    );
  } else if (nav.isLoading || !entry) {
    body = <LoadingState minH="min-h-[40vh]" />;
  } else if (entry.failed) {
    body = (
      <ServerUnavailableState
        compact
        serverId={server!.id}
        serverName={server!.name}
        owned={server!.owned}
      />
    );
  } else if (entry.libraries.length === 0) {
    body = (
      <EmptyState
        minH="min-h-[55vh]"
        icon={<FilmstripIcon size={28} />}
        title={`${server!.name} has no libraries yet.`}
        action={
          server!.owned ? (
            <Link
              to="/settings/server/$serverId/libraries"
              params={{ serverId: server!.id }}
              search={{ setup: 1 }}
              className="rago-btn rago-btn-fit"
            >
              Add a library
            </Link>
          ) : null
        }
      />
    );
  } else {
    body = (
      // Emby's density: as many 16:9 tiles as fit at ~200px (six at 1440,
      // four around 1000), two columns on phones; the row gap leaves room for
      // the two caption lines, the top padding for the hover outline.
      <div className="grid grid-cols-2 gap-x-4 gap-y-6 pt-0.5 sm:gap-x-5 sm:gap-y-7 sm:[grid-template-columns:repeat(auto-fill,minmax(196px,1fr))]">
        {entry.libraries.map((lib) => (
          <LibraryCard
            key={lib.id}
            lib={lib}
            serverId={server!.id}
            art={artFor(lib.id)}
            scan={activity.scanning.get(lib.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="rago-page px-6 pb-10 sm:px-10">
      <h1 className="rago-display mb-2 text-2xl">Library</h1>
      {server ? (
        <p className="mb-8 text-sm text-[var(--rago-text-3)]">From {server.name}</p>
      ) : (
        <div className="mb-8" />
      )}
      {body}
    </div>
  );
}
